import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';
import { useCaseId } from '@/stores/caseStore';
import { pathwayActions } from '@/stores/pathwayStore';
import { settingsActions } from '@/stores/settingsStore';
import featureConfig from '@/featureConfig';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getRoundedPath } from '@/utils/svgUtils';
import PortTooltip from '@/components/nodes/PortTooltip';
import { buildHandle } from '@/utils/pathwayUtils';
import { isSplitterNodeName } from '@/consts';

/**
 * ArrowNode Component
 * Renders a compact, triangular node for Stream Splitters and Combiners.
 *
 * - Splitter: Rendered as a '<' shape (input on left tip, outputs on right base)
 * - Combiner: Rendered as a '>' shape (inputs on left base, output on right tip)
 *
 * The node size scales dynamically based on the number of ports to ensure
 * connectors are evenly spaced.
 */
const ArrowNode = ({ data: node }) => {
  const { id, inputs, outputs, label, name } = node;
  const { projectId, pathwayId, nodeId } = useParams();
  const caseId = useCaseId();
  const navigate = useNavigate();
  const updateNodeInternals = useUpdateNodeInternals();

  const isSplitter = isSplitterNodeName(name);

  // Dynamic Sizing Logic:
  // We use an equilateral triangle geometry.
  // 1. Calculate height based on the number of ports (ensuring enough vertical space).
  const maxPorts = Math.max(inputs?.length || 0, outputs?.length || 0);
  const portSpacing = 100;
  const minHeight = 50;
  const height = Math.max(minHeight, (maxPorts + 1) * portSpacing * 0.35);

  // 2. Calculate width to maintain equilateral proportions (altitude = side * sqrt(3)/2)
  const width = height * (Math.sqrt(3) / 2);

  // Force ReactFlow to update internal handle positions when dimensions change
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, width, height, updateNodeInternals]);

  const removeNode = e => {
    e.stopPropagation();
    pathwayActions.removeNode(id);
    if (nodeId === id) {
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`);
    }
  };

  const handleNodeClick = () => {
    settingsActions.closePalette();
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}/nodes/${id}`, { state: { node } });
  };

  // Generate the SVG path for the rounded triangle
  const pathD = useMemo(() => {
    const radius = 8; // Rounded corner radius
    const points = isSplitter
      ? [
          [0, height / 2],
          [width, 0],
          [width, height],
        ] // Left-pointing triangle vertices
      : [
          [0, 0],
          [width, height / 2],
          [0, height],
        ]; // Right-pointing triangle vertices
    return getRoundedPath(points, radius);
  }, [isSplitter, width, height]);

  return (
    <div className="relative group/item cursor-pointer" style={{ width, height }} onClick={handleNodeClick}>
      {/* Label - Floating above the triangle */}
      <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 flex justify-center pointer-events-none">
        <span className="text-base font-bold text-gray-700 truncate max-w-[300px] text-center bg-white/50 px-1 rounded">
          {label}
        </span>
      </div>

      {/* Delete Button - Positioned at the top vertex of the triangle */}
      {featureConfig.manageModules && (
        <div
          className="absolute z-20 invisible group-hover/item:visible"
          style={{
            top: -12,
            left: isSplitter ? width - 12 : -12,
          }}
        >
          <div
            onClick={removeNode}
            className="p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </div>
        </div>
      )}

      {/* The Shape Layer */}
      <svg
        className="absolute inset-0 w-full h-full drop-shadow-md transition-all duration-200 hover:drop-shadow-lg"
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        <path
          d={pathD}
          fill="white"
          stroke="#374151"
          strokeWidth="1"
          strokeLinejoin="round"
          className="transition-colors duration-200 group-hover/item:stroke-gray-900"
        />
      </svg>

      {/* Inputs Layer - Evenly spaced vertically */}
      <div
        className={`absolute top-0 bottom-0 flex flex-col justify-evenly ${
          isSplitter ? 'left-0 items-start' : 'left-0 items-start'
        }`}
        style={{ width: isSplitter ? '20%' : '30%' }}
      >
        {inputs?.map(input => (
          <div key={input.id} className="relative group/handle flex items-center">
            <Handle
              type="target"
              position={Position.Left}
              id={buildHandle(id, 'input', input.name)}
              className="w-4 h-4 !bg-gray-200 !border-2 !border-black hover:!bg-gray-100 transition-colors"
            />
            <PortTooltip nodeId={id} ioName={input.name} ioLabel={input.label} handleType="input" position="left" />
          </div>
        ))}
      </div>

      {/* Outputs Layer - Evenly spaced vertically */}
      <div
        className={`absolute top-0 bottom-0 flex flex-col justify-evenly ${
          isSplitter ? 'right-0 items-end' : 'right-0 items-end'
        }`}
        style={{ width: isSplitter ? '30%' : '20%', left: isSplitter ? 'auto' : 'auto', right: 0 }}
      >
        {outputs?.map(output => (
          <div key={output.id} className="relative group/handle flex items-center justify-end">
            <Handle
              type="source"
              position={Position.Right}
              id={buildHandle(id, 'output', output.name)}
              className="w-4 h-4 !bg-gray-200 !border-2 !border-black hover:!bg-gray-100 transition-colors"
            />
            <PortTooltip nodeId={id} ioName={output.name} ioLabel={output.label} handleType="output" position="right" />
          </div>
        ))}
      </div>
    </div>
  );
};

ArrowNode.propTypes = {
  data: PropTypes.object,
};

export default ArrowNode;
